import type {
  LockerverseAuction,
  LockerverseAuctionBidResult,
  LockerverseAuctionItem,
  LockerverseAuctionPricing,
} from "@lockerverse/sdk/auctions";
import {
  calculateLockerverseAuctionPricing,
  getLockerverseAuctionMinimumBid,
} from "@lockerverse/sdk/auctions";
import type {
  Stripe,
  StripeAddressElementChangeEvent,
  StripeElements,
} from "@stripe/stripe-js";
import { getAuctionDisplayStatus } from "./auction-display.ts";

export type AuctionSelection = {
  amount: number;
  quantity: number;
  includeServiceFee: boolean;
};

export function getAuctionSelectionPricing(
  auction: LockerverseAuction,
  item: LockerverseAuctionItem,
  selection: AuctionSelection
) {
  return calculateLockerverseAuctionPricing(
    auction,
    item.saleMode === "buy_now"
      ? (item.buyNowPriceAmount ?? 0) * selection.quantity
      : selection.amount,
    selection.includeServiceFee
  );
}

export function getAuctionSelectionError(
  auction: LockerverseAuction,
  item: LockerverseAuctionItem,
  selection: AuctionSelection
) {
  if (getAuctionDisplayStatus(auction, item) !== "active") {
    return "This item is not accepting bids or purchases.";
  }
  if (item.saleMode === "hybrid") {
    return "This item type is not supported for checkout.";
  }
  if (item.saleMode === "buy_now") {
    if (item.isSoldOut || item.availableQuantity === 0) {
      return "This item is sold out.";
    }
    if (
      !(item.buyNowPriceAmount && Number.isSafeInteger(selection.quantity)) ||
      selection.quantity < 1 ||
      (item.availableQuantity !== null &&
        selection.quantity > item.availableQuantity)
    ) {
      return "Select an available quantity.";
    }
  } else if (
    !Number.isSafeInteger(selection.amount) ||
    selection.amount % 100 !== 0 ||
    selection.amount < getLockerverseAuctionMinimumBid(item)
  ) {
    return "Enter a whole-dollar bid at or above the minimum bid.";
  }
  return null;
}

export function hasAuctionCheckoutChanged(
  previous: LockerverseAuction,
  current: LockerverseAuction,
  item: LockerverseAuctionItem,
  nextItem: LockerverseAuctionItem,
  selection: AuctionSelection
) {
  return (
    previous.payment?.publishableKey !== current.payment?.publishableKey ||
    previous.payment?.connectedAccountId !==
      current.payment?.connectedAccountId ||
    previous.serviceFeeEnabled !== current.serviceFeeEnabled ||
    item.saleMode !== nextItem.saleMode ||
    item.collectShippingAddress !== nextItem.collectShippingAddress ||
    JSON.stringify(getAuctionSelectionPricing(previous, item, selection)) !==
      JSON.stringify(getAuctionSelectionPricing(current, nextItem, selection))
  );
}

export function auctionExpectedPricing(pricing: LockerverseAuctionPricing) {
  return {
    expectedServiceFeeCents: pricing.serviceFeeCents,
    expectedSubtotalCents: pricing.subtotalCents,
    expectedTotalCents: pricing.totalCents,
  };
}

export async function createAuctionConfirmationToken({
  stripe,
  elements,
  email,
  phone,
  billing,
  shipping,
  collectShippingAddress,
}: {
  stripe: Stripe;
  elements: StripeElements;
  email: string;
  phone: string;
  billing: StripeAddressElementChangeEvent | null;
  shipping: StripeAddressElementChangeEvent | null;
  collectShippingAddress: boolean;
}) {
  if (!(billing?.complete && billing.value.name.trim())) {
    throw new Error("Complete your billing name and address.");
  }
  if (
    collectShippingAddress &&
    !(shipping?.complete && shipping.value.name.trim())
  ) {
    throw new Error("Complete your shipping name and address.");
  }
  const submitted = await elements.submit();
  if (submitted.error) {
    throw new Error(submitted.error.message ?? "Check your payment details.");
  }
  const result = await stripe.createConfirmationToken({
    elements,
    params: {
      payment_method_data: {
        allow_redisplay: "always",
        billing_details: {
          address: billing.value.address,
          email,
          name: billing.value.name,
          phone,
        },
      },
      return_url: window.location.href,
      ...(collectShippingAddress && shipping
        ? {
            shipping: {
              address: shipping.value.address,
              name: shipping.value.name,
              phone,
            },
          }
        : undefined),
    },
  });
  if (result.error || !result.confirmationToken) {
    throw new Error(
      result.error?.message ?? "Unable to prepare payment details."
    );
  }
  return result.confirmationToken.id;
}

export async function completeAuctionAction(
  stripe: Stripe,
  result: LockerverseAuctionBidResult
) {
  if (!result.requiresAction) {
    return;
  }
  if (!result.clientSecret) {
    throw new Error(
      "Payment confirmation is incomplete. Check its status before trying again."
    );
  }
  const action = await stripe.handleNextAction({
    clientSecret: result.clientSecret,
  });
  if (action.error) {
    throw new Error(
      action.error.message ?? "Payment confirmation is incomplete."
    );
  }
}
